import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Instagram, Facebook, Mail, Phone, MapPin, ArrowRight } from 'lucide-react';

/**
 * Template 20: BAZAAR — Marketplace Vibrant
 * Footer: Magenta gradient footer, white text, 4 columns
 */
const Footer20 = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');

    const handleSubscribe = (e) => {
        e.preventDefault();
        if (email.trim()) setEmail('');
    };

    const shopLinks = [
        { label: 'Sarees', path: '/template20/shop' },
        { label: 'Kurtas', path: '/template20/shop' },
        { label: 'Lehengas', path: '/template20/shop' },
        { label: 'Jewelry', path: '/template20/shop' },
        { label: 'Accessories', path: '/template20/shop' },
    ];

    const customerCareLinks = [
        { label: 'Contact Us', path: '/template20/contact' },
        { label: 'Shipping & Delivery', path: '/template20/shipping-exchange' },
        { label: 'Returns & Exchange', path: '/template20/shipping-exchange' },
        { label: 'FAQs', path: '/template20/faqs' },
        { label: 'Track Order', path: '/template20/orders' },
    ];

    const companyLinks = [
        { label: 'About Us', path: '/template20/about' },
        { label: 'Privacy Policy', path: '/template20/privacy-policy' },
        { label: 'Terms & Conditions', path: '/template20/terms-conditions' },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600;700&family=Nunito:wght@300;400;600&display=swap');
            `}</style>

            <footer
                style={{
                    background: 'linear-gradient(135deg, #86198F 0%, #A21CAF 30%, #C026D3 60%, #D946EF 100%)',
                }}
            >
                {/* Newsletter Strip */}
                <div className="border-b border-white/15">
                    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 sm:py-10">
                        <div className="flex flex-col md:flex-row items-center justify-between gap-5">
                            <div className="text-center md:text-left">
                                <h3
                                    className="text-xl sm:text-2xl font-bold text-white mb-1"
                                    style={{ fontFamily: "'Rubik', sans-serif" }}
                                >
                                    Join the Bazaar Club
                                </h3>
                                <p className="text-white/70 text-sm" style={{ fontFamily: "'Nunito', sans-serif" }}>
                                    Get exclusive deals, new arrivals & 10% off your first order
                                </p>
                            </div>
                            <form onSubmit={handleSubscribe} className="flex w-full md:w-auto max-w-md">
                                <input
                                    type="email"
                                    placeholder="Your email address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="flex-1 px-4 py-3 bg-white/15 border border-white/20 rounded-l-full text-white placeholder-white/50 text-sm outline-none focus:bg-white/20 transition-colors"
                                    style={{ fontFamily: "'Nunito', sans-serif" }}
                                />
                                <button
                                    type="submit"
                                    className="px-6 py-3 bg-white text-[#C026D3] rounded-r-full font-semibold text-sm hover:bg-[#FDF4FF] transition-colors flex items-center gap-2"
                                    style={{ fontFamily: "'Nunito', sans-serif" }}
                                >
                                    Subscribe <ArrowRight className="w-4 h-4" />
                                </button>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Main Footer Grid */}
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10 sm:py-14">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6">
                        {/* Column 1: Brand */}
                        <div className="col-span-2 md:col-span-1">
                            <h2
                                className="text-2xl font-bold text-white mb-3 tracking-tight"
                                style={{ fontFamily: "'Rubik', sans-serif" }}
                            >
                                BAZAAR
                            </h2>
                            <p className="text-white/70 text-sm leading-relaxed mb-5" style={{ fontFamily: "'Nunito', sans-serif" }}>
                                Your vibrant marketplace for the finest Indian fashion. From traditional sarees to contemporary styles, discover it all.
                            </p>
                            <div className="flex gap-3">
                                <a href="#" className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-white hover:bg-white hover:text-[#C026D3] transition-all duration-300">
                                    <Instagram className="w-4 h-4" />
                                </a>
                                <a href="#" className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-white hover:bg-white hover:text-[#C026D3] transition-all duration-300">
                                    <Facebook className="w-4 h-4" />
                                </a>
                                <a href="#" className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center text-white hover:bg-white hover:text-[#C026D3] transition-all duration-300">
                                    <Mail className="w-4 h-4" />
                                </a>
                            </div>
                        </div>

                        {/* Column 2: Shop */}
                        <div>
                            <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4" style={{ fontFamily: "'Rubik', sans-serif" }}>
                                Shop
                            </h4>
                            <ul className="space-y-2.5">
                                {shopLinks.map((link) => (
                                    <li key={link.label}>
                                        <button
                                            onClick={() => navigate(link.path)}
                                            className="text-sm text-white/65 hover:text-white transition-colors"
                                            style={{ fontFamily: "'Nunito', sans-serif" }}
                                        >
                                            {link.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Column 3: Customer Care */}
                        <div>
                            <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4" style={{ fontFamily: "'Rubik', sans-serif" }}>
                                Customer Care
                            </h4>
                            <ul className="space-y-2.5">
                                {customerCareLinks.map((link) => (
                                    <li key={link.label}>
                                        <button
                                            onClick={() => navigate(link.path)}
                                            className="text-sm text-white/65 hover:text-white transition-colors"
                                            style={{ fontFamily: "'Nunito', sans-serif" }}
                                        >
                                            {link.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Column 4: Company */}
                        <div>
                            <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4" style={{ fontFamily: "'Rubik', sans-serif" }}>
                                Company
                            </h4>
                            <ul className="space-y-2.5">
                                {companyLinks.map((link) => (
                                    <li key={link.label}>
                                        <button
                                            onClick={() => navigate(link.path)}
                                            className="text-sm text-white/65 hover:text-white transition-colors"
                                            style={{ fontFamily: "'Nunito', sans-serif" }}
                                        >
                                            {link.label}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-5 space-y-2">
                                <div className="flex items-center gap-2 text-white/65 text-xs">
                                    <Phone className="w-3.5 h-3.5" />
                                    <span style={{ fontFamily: "'Nunito', sans-serif" }}>+91 98765 43210</span>
                                </div>
                                <div className="flex items-center gap-2 text-white/65 text-xs">
                                    <Mail className="w-3.5 h-3.5" />
                                    <span style={{ fontFamily: "'Nunito', sans-serif" }}>hello@bazaar.in</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="border-t border-white/15">
                    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
                        <p className="text-white/50 text-xs" style={{ fontFamily: "'Nunito', sans-serif" }}>
                            &copy; {new Date().getFullYear()} Bazaar. All rights reserved.
                        </p>
                        <div className="flex items-center gap-4 text-white/50 text-xs" style={{ fontFamily: "'Nunito', sans-serif" }}>
                            <span className="cursor-pointer hover:text-white transition-colors" onClick={() => navigate('/template20/privacy-policy')}>Privacy</span>
                            <span className="cursor-pointer hover:text-white transition-colors" onClick={() => navigate('/template20/terms-conditions')}>Terms</span>
                            <span className="cursor-pointer hover:text-white transition-colors" onClick={() => navigate('/template20/shipping-exchange')}>Shipping</span>
                        </div>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default Footer20;
